// Decides when to tell the backend that the user is actually using the app.
//
// The active-user metric counts a user as active on a day if they opened the
// app or did something in it. A tab merely left open does not count, and
// neither does our own code refetching in the background. The backend cannot
// tell those apart (a TanStack Query refetch on window refocus looks exactly
// like a page load), so the frontend decides and signals with X-App-Boot.
//
// Two triggers, both requiring a real user action:
//   1. A page load. The module reloads, so `bootPending` is true again.
//   2. An in-app navigation on a day we have not marked yet, which covers a
//      tab left open across midnight. See useActivityMark.
//
// A window refocus is neither, which is the point.
//
// The signal rides on our own API request, so ad blockers and analytics
// opt-outs never strip it. That is what makes it work for the ~12% of users
// whose posthog-js never loads.

let bootPending = true;
let lastMarkedDate: string | null = null;

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * True for the first settings fetch after a real page load. Consumes the flag.
 */
export function consumeAppBootFlag(): boolean {
  if (!bootPending) return false;
  bootPending = false;
  lastMarkedDate = utcToday();
  return true;
}

/**
 * True when the user has navigated on a UTC day we have not marked active yet,
 * which happens when a tab stays open past midnight. Claims the day, so the
 * caller must actually send the request.
 */
export function claimActivityMark(): boolean {
  const today = utcToday();
  if (lastMarkedDate === today) return false;
  lastMarkedDate = today;
  return true;
}

// Test-only: restore the pre-boot state between cases.
export function resetAppBootFlagForTests(): void {
  bootPending = true;
  lastMarkedDate = null;
}
